import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { ButtonBase, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { MouseEvent } from "react";

import { Icon } from "@/components/ui-v2/Icon/Icon";
import { IconButton } from "@/components/ui-v2/IconButton/IconButton";
import { useDoubleClick } from "@/hooks/useDoubleClick";

import { removeExtension } from "../../../../utils";

interface PoseTileProps {
  id: string;
  name: string;
  selected: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
  openPoseImageUrl: string;
  showcaseImageUrl?: string;
  onMenuOpen: (event: MouseEvent) => any;
}

const PoseImageContainer = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ selected, theme }) => ({
  position: "relative",
  border: "3px solid transparent",
  backgroundColor: "transparent",
  aspectRatio: 1,
  maxHeight: "100%",
  maxWidth: "100%",
  borderRadius: 9,
  overflow: "hidden",

  ...(selected && {
    borderColor: theme.palette.primary.light,

    "&:hover": {
      borderColor: theme.palette.primary.main,
    },

    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      transition: "background-color 0.3s ease",
      zIndex: 1,
    },
  }),
}));

const PoseImage = styled("img")({
  objectFit: "contain",
  width: "100%",
  height: "auto",
});

const PoseImageHover = styled("img")(({ theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  opacity: 0,
  width: "100%",
  height: "auto",
  transition: theme.transitions.create(["opacity"]),
  zIndex: 2,

  "&:hover": {
    opacity: 1,
  },
}));

const PoseName = styled(Typography)({
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
});

export const PoseTile = (props: PoseTileProps) => {
  const {
    name,
    openPoseImageUrl,
    showcaseImageUrl,
    selected,
    onClick,
    onDoubleClick,
    onMenuOpen,
  } = props;

  const handleClick = useDoubleClick({
    onSingleClick: onClick,
    onDoubleClick,
  });

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    onMenuOpen(event);
  };

  return (
    <Stack spacing={1} onContextMenu={handleContextMenu}>
      <PoseImageContainer selected={selected} onClick={handleClick}>
        <PoseImage src={showcaseImageUrl || openPoseImageUrl} alt={name} />
        {showcaseImageUrl && (
          <PoseImageHover src={openPoseImageUrl} alt={name} />
        )}
      </PoseImageContainer>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <PoseName variant="micro" fontWeight="bold">
          {removeExtension(name)}
        </PoseName>
        <IconButton
          variant="quaternary"
          aria-label="Open pose options"
          onClick={onMenuOpen}
        >
          <Icon size="medium" icon={faEllipsisVertical} />
        </IconButton>
      </Stack>
    </Stack>
  );
};
